import { EbMode } from '../sim/systems/enemyBulletSystem';

/* ═══════════════════════════════════════════════════════════════════════════
   LE STYLE DE RENDU D'UNE BALLE ENNEMIE, dérivé de sa CONDUITE (`eb.mode`).

   Jusqu'ici toutes les balles ennemies se dessinaient pareil : un point qui
   avance, quelle que soit son arme. La simulation, elle, porte depuis longtemps
   quatre conduites distinctes (`EbMode`) — et le joueur ne pouvait pas les lire.
   Ce module traduit une conduite en FORME, façon Ratchet & Clank : le laser est
   une barre, le disque tourne, le missile pointe, la spirale spirale. La couleur
   n'arrive qu'en renfort ; c'est la SILHOUETTE qui porte l'information, ce qui
   reste vrai en vision périphérique et daltonienne (PLAN.md §4.4).

   ── Pourquoi un helper PUR ────────────────────────────────────────────────
   `styleFor` ne dépend que de (mode, warn, param) : aucune horloge, aucune
   vitesse, aucun three.js. La rotation propre du disque et le crépitement du
   faisceau — qui, eux, dépendent du temps — sont appliqués par `View`, à partir
   du DESCRIPTEUR que ce module retourne. Cela rend le mapping conduite → forme
   testable sans GPU (`tests/view/bullet-style.test.ts`).

   ── Zéro allocation par balle ──────────────────────────────────────────────
   ⚠️ `View.sync` appelle `styleFor` une fois par balle et par frame. La fonction
   ne construit donc RIEN : elle renvoie l'un des descripteurs figés ci-dessous.
   Un objet neuf par balle serait un passage du ramasse-miettes au pire moment,
   celui où l'écran est plein.

   ── Le cas du DISQUE ───────────────────────────────────────────────────────
   Il n'existe PAS de conduite « disque » côté ennemi : les modes sont
   Ballistic, Homing, Curve et Beam (voir `EbMode`). Le seul projectile à
   TRAJECTOIRE COURBE est la Curve (le Tourbillon), et c'est lui qui reçoit le
   rendu de DISQUE tournant. Ce choix couvre d'un coup les deux demandes :
     · « disque tranchant qui tourne vite » → la ROTATION PROPRE du sprite
       (`spin ≠ 0`), appliquée par `View` sur l'horloge de tick ;
     · « spirale : l'orientation suit la courbe » → les BRAS SPIRALÉS, déjà
       dessinés par la TRAJECTOIRE que la simulation courbe.
   Le disque tourne donc sur lui-même pendant que la volée entière déroule sa
   spirale — la lame tranchante ET la spirale, sur le seul projectile qui courbe.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Forme d'une balle ennemie. La valeur EST envoyée telle quelle au shader
 *  (`aShape`), qui branche dessus. `Orb` reste le rendu historique : toute
 *  balle balistique (l'écrasante majorité) le garde, au pixel près. */
export const BulletShape = {
  /** Orbe à zones concentriques — le rendu d'origine, inchangé. */
  Orb: 0,
  /** Faisceau laser : barre verticale, noyau vif, crépitement électrique. */
  Beam: 1,
  /** Disque tranchant : anneau denté qui tourne vite sur lui-même. */
  Disc: 2,
  /** Missile guidé : dard orienté sur sa vitesse, avec une traînée. */
  Missile: 3,
} as const;

export interface BulletStyle {
  /** Forme envoyée au shader — une valeur de `BulletShape`. */
  readonly shape: number;
  /** Multiplicateur de LARGEUR appliqué au rayon visuel de référence (r × 3,4).
   *  1 = orbe historique. */
  readonly size: number;
  /** Allongement de BASE (hauteur = largeur × len). L'orbe part de 1 ; le
   *  faisceau est plus haut que large, le missile allongé. */
  readonly len: number;
  /** Gain d'allongement PAR LA VITESSE, réservé à l'orbe : c'est ce qui fait
   *  lire la menace dans la forme d'une balle rapide. Nul sur les formes
   *  exotiques, dont la silhouette porte déjà l'information. */
  readonly speedLen: number;
  /** Rotation PROPRE en radians par seconde. ≠ 0 pour le disque uniquement ;
   *  `View` la multiplie par l'horloge de tick. Le signe donne le sens. */
  readonly spin: number;
  /** `true` : le sprite s'oriente sur la VITESSE (orbe, missile). `false` : le
   *  sprite garde une orientation fixe/propre (faisceau vertical, disque qui
   *  tourne sur son axe). */
  readonly follow: boolean;
  /** Multiplicateur de TEINTE. 1 = teinte de palette exacte. Le faisceau en
   *  charge part plus sombre (avertissement) ; `View` y ajoute le crépitement. */
  readonly tint: number;
}

/* ── Les descripteurs figés ────────────────────────────────────────────────
   Un objet par forme (deux pour le disque : un par sens de rotation). Gelés
   pour interdire toute mutation accidentelle depuis la boucle de rendu. */

/** Balle ordinaire. ⚠️ Ces valeurs REPRODUISENT le rendu historique au pixel
 *  près : `size = 1` (donc largeur = r × 3,4), `len = 1 + vitesse × 0,00042`,
 *  orientation sur la vitesse. Y toucher change TOUTES les balles ordinaires. */
const ORB: BulletStyle = Object.freeze({
  shape: BulletShape.Orb,
  size: 1,
  len: 1,
  speedLen: 0.00042,
  spin: 0,
  follow: true,
  tint: 1,
});

/** Missile guidé (Homing). Dard fin et allongé, orienté sur sa vitesse : la
 *  proue montre où il va, la traînée d'où il vient. Tête un peu plus chaude. */
const MISSILE: BulletStyle = Object.freeze({
  shape: BulletShape.Missile,
  size: 1.0,
  len: 2.2,
  speedLen: 0,
  spin: 0,
  follow: true,
  tint: 1.15,
});

/** Disque tranchant (Curve) — sens horaire. Plus large qu'une orbe, rotation
 *  propre soutenue (≈ 2,2 tour/s). Pas d'orientation sur la vitesse : c'est le
 *  spin qui compte, et la spirale vient de la trajectoire. */
const DISC_CW: BulletStyle = Object.freeze({
  shape: BulletShape.Disc,
  size: 1.35,
  len: 1,
  speedLen: 0,
  spin: 14,
  follow: false,
  tint: 1,
});

/** Disque tranchant — sens anti-horaire (courbe de signe opposé). */
const DISC_CCW: BulletStyle = Object.freeze({ ...DISC_CW, spin: -14 });

/** Faisceau laser ACTIF (Beam, hors avertissement). Barre plus étroite que
 *  l'orbe (largeur ≈ diamètre d'un segment, les colonnes se touchent) et plus
 *  haute que large (chaque segment recouvre le pas de colonne → lame pleine). */
const BEAM_ACTIVE: BulletStyle = Object.freeze({
  shape: BulletShape.Beam,
  size: 0.62,
  len: 1.7,
  speedLen: 0,
  spin: 0,
  follow: false,
  tint: 1,
});

/** Faisceau EN CHARGE (Beam, `warn > 0`). Nettement plus FIN et plus SOMBRE que
 *  l'actif : il « charge » sans encore trancher. `View` ajoute le clignotement
 *  qui le fait crépiter. C'est ce qui distingue l'avertissement du coup porté. */
const BEAM_WARN: BulletStyle = Object.freeze({
  shape: BulletShape.Beam,
  size: 0.34,
  len: 1.5,
  speedLen: 0,
  spin: 0,
  follow: false,
  tint: 0.7,
});

/**
 * Descripteur de rendu d'une balle ennemie, d'après sa conduite.
 *
 * @param mode  une valeur de `EbMode` (`eb.mode`).
 * @param warn  avertissement restant du faisceau (`eb.warn`) : > 0 = en charge.
 * @param param paramètre de conduite (`eb.param`). Son SIGNE donne le sens de
 *              rotation du disque (Curve) ; ignoré pour les autres modes.
 */
export function styleFor(mode: number, warn: number, param: number): BulletStyle {
  switch (mode) {
    case EbMode.Homing:
      return MISSILE;
    case EbMode.Curve:
      // Le disque tourne dans le sens où la trajectoire courbe.
      return param < 0 ? DISC_CCW : DISC_CW;
    case EbMode.Beam:
      return warn > 0 ? BEAM_WARN : BEAM_ACTIVE;
    default:
      // Ballistic et tout mode inconnu : l'orbe, inchangée.
      return ORB;
  }
}
